import type {
	DashboardtypesLinkDTO,
	DashboardtypesAxesDTO,
	DashboardtypesComparisonThresholdDTO,
	DashboardtypesFillModeDTO,
	DashboardtypesFillOpacityDTO,
	DashboardtypesHeatmapAxesDTO,
	DashboardtypesHeatmapColorsDTO,
	DashboardtypesHeaderOptionsDTO,
	DashboardtypesHistogramBucketsDTO,
	DashboardtypesLegendDTO,
	DashboardtypesLineInterpolationDTO,
	DashboardtypesLineStyleDTO,
	DashboardtypesPanelFormattingDTO,
	DashboardtypesPanelSpecDTO,
	DashboardtypesSpanGapsDTO,
	DashboardtypesStackModeDTO,
	DashboardtypesTableFormattingDTO,
	DashboardtypesTableThresholdDTO,
	DashboardtypesTextPresentationDTO,
	DashboardtypesThresholdWithLabelDTO,
	DashboardtypesTimePreferenceDTO,
	TelemetrytypesTelemetryFieldKeyDTO,
} from 'api/generated/services/sigNoz.schemas';
import type { LegendSeriesResolver } from '../utils/legendSeries';
import {
	Antenna,
	BarChart,
	Columns3,
	Hash,
	Link2,
	Palette,
	PanelTop,
	PencilRuler,
	Scale3D,
	Signpost,
	Wallpaper,
	AlignLeft,
} from '@signozhq/icons';

// Derived from an actual icon component so the type stays exact (size is a
// constrained IconSize union, not arbitrary strings) and ForwardRef-compatible.
export type SectionIcon = typeof Hash;

export interface SectionMetadata {
	title: string;
	icon: SectionIcon;
	description?: string;
}

/**
 * Discriminant for each config section (the `kind` field of a `SectionConfig`). The
 * string values match the keys each slice persists under in the plugin spec.
 */
export enum SectionKind {
	Formatting = 'formatting',
	Axes = 'axes',
	Legend = 'legend',
	ChartAppearance = 'chartAppearance',
	Buckets = 'buckets',
	Visualization = 'visualization',
	Thresholds = 'thresholds',
	ContextLinks = 'contextLinks',
	Columns = 'columns',
	TextLayout = 'presentation',
	PanelHeader = 'headerOptions',
}

/**
 * Which threshold editor a kind uses. All three variants persist to the same
 * `plugin.spec.thresholds` key with different element shapes:
 * - `label` — value + color + label lines (TimeSeries / Bar)
 * - `comparison` — value crosses an operator → recolor (Number)
 * - `table` — per-column comparison (Table)
 */
export enum ThresholdVariant {
	LABEL = 'label',
	COMPARISON = 'comparison',
	TABLE = 'table',
}

/** Union of every threshold element shape stored under `plugin.spec.thresholds`. */
export type AnyThreshold =
	| DashboardtypesThresholdWithLabelDTO
	| DashboardtypesComparisonThresholdDTO
	| DashboardtypesTableThresholdDTO;

/**
 * Each section ↔ one slice of the panel spec it edits. Most slices live under
 * `spec.plugin.spec.<key>`; `contextLinks` is panel-level (`spec.links`).
 */
// Superset spanning every kind's formatting DTO; the `controls` bag gates which
// fields a kind actually writes.
export type PanelFormattingSlice = DashboardtypesPanelFormattingDTO &
	Pick<DashboardtypesTableFormattingDTO, 'columnUnits'>;

/** Superset spanning every kind's chart-appearance DTO. */
export interface PanelChartAppearanceSlice {
	lineStyle?: DashboardtypesLineStyleDTO;
	lineInterpolation?: DashboardtypesLineInterpolationDTO;
	/** Area's wire enum is a nominally distinct subset with the same members. */
	fillMode?: DashboardtypesFillModeDTO;
	fillOpacity?: DashboardtypesFillOpacityDTO;
	showPoints?: boolean;
	spanGaps?: DashboardtypesSpanGapsDTO;
	/** The heatmap's cell colour ramp. */
	colors?: DashboardtypesHeatmapColorsDTO;
}

/** Superset spanning every kind's visualization DTO. */
export interface PanelVisualizationSlice {
	timePreference?: DashboardtypesTimePreferenceDTO;
	/** Bar stacking; a kind declares this or `stack`, never both. */
	stackedBarChart?: boolean;
	/** Area stacking. */
	stack?: DashboardtypesStackModeDTO;
	fillSpans?: boolean;
}

// Superset of every kind's axes DTO, gated by the `controls` bag.
export type PanelAxesSlice = DashboardtypesAxesDTO &
	DashboardtypesHeatmapAxesDTO;

export interface SectionSpecMap {
	[SectionKind.Formatting]: PanelFormattingSlice; // spec.plugin.spec.formatting
	[SectionKind.Axes]: PanelAxesSlice; // spec.plugin.spec.axes
	[SectionKind.Legend]: DashboardtypesLegendDTO; // spec.plugin.spec.legend
	[SectionKind.ChartAppearance]: PanelChartAppearanceSlice; // spec.plugin.spec.chartAppearance
	[SectionKind.Buckets]: DashboardtypesHistogramBucketsDTO; // spec.plugin.spec.histogramBuckets
	// spec.plugin.spec.visualization — typed as the superset of every kind's shape;
	// the `controls` bag gates which fields each kind writes.
	[SectionKind.Visualization]: PanelVisualizationSlice;
	[SectionKind.Thresholds]: AnyThreshold[]; // spec.plugin.spec.thresholds (variant picks the editor)
	[SectionKind.ContextLinks]: DashboardtypesLinkDTO[]; // spec.links (PANEL-level)
	[SectionKind.Columns]: TelemetrytypesTelemetryFieldKeyDTO[]; // spec.plugin.spec.selectFields (List)
	[SectionKind.TextLayout]: DashboardtypesTextPresentationDTO; // spec.plugin.spec.presentation (Text)
	[SectionKind.PanelHeader]: DashboardtypesHeaderOptionsDTO; // spec.plugin.spec.headerOptions (Text)
}

/**
 * Controlled sections — a kind exposes a subset of the section's controls (V2
 * analogue of V1's `allowSoftMinMax` / `allowLegendColors` flags).
 */
export interface SectionControls {
	[SectionKind.Formatting]: {
		unit?: boolean;
		decimals?: boolean;
		columnUnits?: boolean;
	};
	// minMax → softMin/softMax; y → the heatmap's bucket-axis scale, whose domain
	// comes from the response and so takes no soft bounds.
	[SectionKind.Axes]: {
		minMax?: boolean;
		logScale?: boolean;
		y?: boolean;
	};
	[SectionKind.Legend]: {
		position?: boolean;
		// colors → customColors; the resolver supplies the labels overrides are keyed by,
		// so a kind can't offer color overrides with nothing to color
		colors?: LegendSeriesResolver;
	};
	[SectionKind.ChartAppearance]: {
		lineStyle?: boolean;
		lineInterpolation?: boolean;
		fillMode?: boolean;
		/**
		 * Declaring it also marks the kind always-filled: `fillMode` drops `none` to match
		 * the narrower `AreaFillMode` wire enum the save API validates against.
		 */
		fillOpacity?: boolean;
		showPoints?: boolean;
		spanGaps?: boolean;
		// colors → the heatmap's cell colour ramp; nothing else here applies to a grid.
		colors?: boolean;
	};
	[SectionKind.Buckets]: {
		count?: boolean;
		width?: boolean;
		mergeQueries?: boolean;
	};
	// switchPanelKind → the visualization-type switcher (every kind, so you can switch
	// away from any panel); stacking → stackedBarChart (Bar); stackMode → stack
	// (Area); fillSpans → fill gaps with 0 (TimeSeries / Area).
	[SectionKind.Visualization]: {
		switchPanelKind: boolean;
		timePreference?: boolean;
		stacking?: boolean;
		stackMode?: boolean;
		fillSpans?: boolean;
	};
	// Editor discriminator (not a spec field): which threshold variant a kind edits.
	[SectionKind.Thresholds]: { variant?: ThresholdVariant };
}

export type ControlledSectionKind = keyof SectionControls;

/** Atomic sections — no sub-controls; a kind either shows them or not. */
export type AtomicSectionKind =
	| SectionKind.ContextLinks
	| SectionKind.Columns
	| SectionKind.TextLayout
	| SectionKind.PanelHeader;

/** Predicate to hide a section from the current spec; returning true removes it. */
export type SectionVisibilityPredicate = (
	spec: DashboardtypesPanelSpecDTO,
) => boolean;

/**
 * What a kind declares in `kinds/<Kind>/sections.ts`: a controlled section with
 * its `controls` subset, or an atomic section bare (`{ kind }`).
 */
export type SectionConfig =
	| {
			[K in ControlledSectionKind]: {
				kind: K;
				controls: SectionControls[K];
				isHidden?: SectionVisibilityPredicate;
			};
	  }[ControlledSectionKind]
	| { kind: AtomicSectionKind; isHidden?: SectionVisibilityPredicate };

// Per-section title + sidebar icon. Pure data; the editor component + spec lens
// live in the ConfigPane section registry.
export const SECTION_METADATA = {
	[SectionKind.Formatting]: { title: 'Formatting & Units', icon: PencilRuler },
	[SectionKind.Axes]: { title: 'Axes', icon: Scale3D },
	[SectionKind.Legend]: { title: 'Legend', icon: Signpost },
	[SectionKind.ChartAppearance]: { title: 'Chart appearance', icon: Palette },
	[SectionKind.Visualization]: { title: 'Visualization', icon: Wallpaper },
	[SectionKind.Buckets]: { title: 'Buckets', icon: BarChart },
	[SectionKind.Thresholds]: { title: 'Thresholds', icon: Antenna },
	[SectionKind.ContextLinks]: { title: 'Context Links', icon: Link2 },
	[SectionKind.Columns]: { title: 'Columns', icon: Columns3 },
	[SectionKind.TextLayout]: { title: 'Panel appearance', icon: AlignLeft },
	[SectionKind.PanelHeader]: { title: 'Panel header', icon: PanelTop },
} as const satisfies Record<SectionKind, SectionMetadata>;

/**
 * Props every section editor receives: its slice (`value`), an `onChange`, and
 * (controlled sections only) the per-kind `controls` subset.
 */
export type SectionEditorProps<K extends SectionKind> = {
	value: SectionSpecMap[K] | undefined;
	/** The kind's seeded value for this slice; what an unset field renders as. */
	defaultValue?: SectionSpecMap[K];
	/** The slice as last saved; Reset and changed markers compare against it. */
	savedValue?: SectionSpecMap[K];
	onChange: (next: SectionSpecMap[K]) => void;
} & (K extends ControlledSectionKind
	? { controls: SectionControls[K] }
	: unknown);

export type SectionControlsOf<K extends SectionKind> =
	K extends ControlledSectionKind ? SectionControls[K] : undefined;
